import { Fragment, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CaretRight, DownloadSimple } from "@phosphor-icons/react";
import { GiftBadge } from "@/components/souvenirs/GiveToMembers";
import { toast } from "sonner";

const ITEMS = { tshirts: { label: "T-shirts", bar: "bg-primary", soft: "bg-primary/70", light: "bg-primary/45" }, cloth: { label: "Cloth", bar: "bg-success", soft: "bg-success/70", light: "bg-success/45" } };
const STRIPES = { backgroundImage: "repeating-linear-gradient(135deg, rgba(255,255,255,.45) 0 3px, transparent 3px 7px)" };
const STEPS = [["sent", "Sent"], ["received", "Received"], ["given", "Given to members"], ["confirmed", "Confirmed by members"]];
const FLAGS = {
  awaiting: { label: "Awaiting receipt", cls: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200" },
  shortfall: { label: "Shortfall", cls: "bg-destructive/10 text-destructive" },
  members_pending: { label: "Members pending", cls: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200" },
  problems: { label: "Problems", cls: "bg-destructive/10 text-destructive" },
  complete: { label: "Fully confirmed", cls: "bg-success/15 text-success" },
};

const fmt = (v) => (v === null || v === undefined ? "Awaiting" : v);

function SegBar({ s, item }) {
  const total = Math.max(s.sent, s.received || 0, 1);
  const w = (n) => `${Math.max(0, n) * 100 / total}%`;
  const rec = s.received || 0;
  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-background ring-1 ring-inset ring-foreground/25 shadow-inner" title="Confirmed · Given · Received · Sent">
      <div className={`${ITEMS[item].bar} transition-[width] duration-500`} style={{ width: w(s.confirmed) }} />
      <div className={`${ITEMS[item].soft} transition-[width] duration-500`} style={{ width: w(s.given - s.confirmed), ...STRIPES }} />
      <div className={`${ITEMS[item].light} transition-[width] duration-500`} style={{ width: w(rec - s.given) }} />
    </div>
  );
}

function SummaryCards({ summary, items }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {items.map((i) => (
        <Card key={i} className={`p-5 border-t-[3px] ${i === "tshirts" ? "border-t-primary" : "border-t-success"}`} data-testid={`progress-summary-${i}`}>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{ITEMS[i].label}</p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {STEPS.map(([k, label]) => (
              <div key={k}>
                <p className="font-display text-2xl font-bold tabular-nums" data-testid={`progress-summary-${i}-${k}`}>{fmt(summary[i][k])}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4"><SegBar s={summary[i]} item={i} /></div>
          {summary[i].held !== undefined && summary[i].held !== null && <p className="text-xs text-muted-foreground mt-2">Held at region: {summary[i].held}</p>}
        </Card>
      ))}
    </div>
  );
}

const Flags = ({ flags, testId }) => (
  <div className="flex flex-wrap gap-1" data-testid={testId}>
    {flags.map((f) => <span key={f} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${FLAGS[f].cls}`}>{FLAGS[f].label}</span>)}
  </div>
);

function Row({ row, items, depth = 0, expandable, open, onToggle }) {
  return (
    <div className={`py-3 ${depth ? "bg-muted/40 border-l-4 border-l-primary/40" : ""}`} data-testid={`progress-row-${row.key}`}>
      <div className="flex items-start justify-between gap-3 px-5">
      <div className={depth ? "pl-4" : ""}>
        {expandable ? (
          <button type="button" onClick={onToggle} className="flex items-center gap-1.5 font-medium text-left hover:text-primary transition-colors" data-testid={`progress-expand-${row.key}`}>
            <CaretRight size={14} weight="bold" className={`transition-transform duration-200 ${open ? "rotate-90" : ""}`} />{row.label}
            <span className="text-xs font-normal text-muted-foreground">({row.children.length})</span>
          </button>
        ) : <p className="font-medium">{row.label} {row.type === "chapter" && <span className="text-xs text-muted-foreground">(chapter)</span>}</p>}
        <div className="mt-1.5"><Flags flags={row.flags} testId={`progress-flags-${row.key}`} /></div>
      </div>
      <p className="font-display text-lg font-bold tabular-nums" data-testid={`progress-pct-${row.key}`}>{row.pct}%</p>
      </div>
      <div className="mt-2 grid gap-4 lg:grid-cols-2">
      {items.map((i) => (
        <div key={i} className="space-y-1.5 px-5">
          <p className="text-xs text-muted-foreground lg:hidden">{ITEMS[i].label}</p>
          <p className="text-xs tabular-nums">
            <span className="font-semibold">{row[i].sent}</span> sent · <span className="font-semibold">{fmt(row[i].received)}</span> recd · <span className="font-semibold">{row[i].given}</span> given · <span className="font-semibold">{row[i].confirmed}</span> conf.
          </p>
          <SegBar s={row[i]} item={i} />
          {row[i].held !== undefined && row[i].held !== null && <p className="text-[11px] text-muted-foreground">Held at region: {row[i].held}</p>}
        </div>
      ))}
      </div>
    </div>
  );
}

export function DistributionProgress() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [item, setItem] = useState("both");
  const [flag, setFlag] = useState("all");
  const [region, setRegion] = useState("all");
  const [open, setOpen] = useState({});
  useEffect(() => { api.get("/souvenirs/chain-progress").then((r) => setData(r.data)).catch(() => setData({ error: true })); }, []);
  const items = item === "both" ? ["tshirts", "cloth"] : [item];
  const canExport = ["regional_manager", "national_manager", "super_admin"].includes(user.role);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const keep = (r) => flag === "all" || r.flags.includes(flag);
    return data.rows.filter((r) => region === "all" || r.region === region)
      .map((r) => ({ ...r, shownChildren: (r.children || []).filter(keep) }))
      .filter((r) => keep(r) || r.shownChildren.length);
  }, [data, flag, region]);

  const exportXlsx = async () => {
    try {
      const params = { item, ...(flag !== "all" ? { flag } : {}), ...(region !== "all" ? { region } : {}) };
      const res = await api.get("/souvenirs/chain-progress/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "souvenir_distribution_progress.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Export failed"); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (data.error) return <p className="text-sm text-destructive" data-testid="progress-error">Could not load distribution progress.</p>;
  const summary = data.scope === "national" && region !== "all" ? data.rows.find((r) => r.region === region) || data.summary : data.summary;

  return (
    <div className="space-y-6" data-testid="distribution-progress">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground mr-auto">
          {data.scope === "national" ? "Every region, then each facility and chapter, down to member confirmations." : data.scope === "region" ? `${data.label} Region: each facility and chapter, down to member confirmations.` : `${data.label}: stock received and member confirmations.`}
        </p>
        <Select value={item} onValueChange={setItem}>
          <SelectTrigger className="w-36" data-testid="progress-item-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="both">Both items</SelectItem><SelectItem value="tshirts">T-shirts</SelectItem><SelectItem value="cloth">Cloth</SelectItem></SelectContent>
        </Select>
        {data.scope !== "pool" && (
          <Select value={flag} onValueChange={setFlag}>
            <SelectTrigger className="w-44" data-testid="progress-flag-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{Object.entries(FLAGS).map(([k, f]) => <SelectItem key={k} value={k}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {data.scope === "national" && (
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-44" data-testid="progress-region-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All regions</SelectItem>{data.rows.map((r) => <SelectItem key={r.region} value={r.region}>{r.region}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {canExport && <Button variant="outline" className="gap-2" onClick={exportXlsx} data-testid="progress-export"><DownloadSimple size={16} weight="bold" /> Export</Button>}
      </div>

      <SummaryCards summary={summary} items={items} />

      {data.scope !== "pool" && (
        <Card className="overflow-hidden divide-y divide-border" data-testid="progress-rows">
          {rows.map((r) => data.scope === "national" ? (
            <Fragment key={r.key}>
              <Row row={r} items={items} expandable={r.children.length > 0} open={!!open[r.key]} onToggle={() => setOpen({ ...open, [r.key]: !open[r.key] })} />
              {open[r.key] && r.shownChildren.map((c) => <Row key={c.key} row={c} items={items} depth={1} />)}
            </Fragment>
          ) : <Row key={r.key} row={r} items={items} />)}
          {!rows.length && <p className="p-8 text-center text-sm text-muted-foreground">{data.scope === "region" ? "Nothing has been sent to facilities or chapters yet." : "No souvenir dispatches match this view."}</p>}
        </Card>
      )}

      {data.scope === "pool" && (
        <Card className="p-5" data-testid="progress-members">
          <h3 className="font-display text-lg font-bold">Members</h3>
          <Flags flags={data.summary.flags} testId="progress-flags-pool" />
          <ul className="mt-3 divide-y divide-border">
            {data.members.map((m, idx) => (
              <li key={idx} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`progress-member-${idx}`}>
                <span><span className="font-medium">{m.name}</span> <span className="text-xs font-mono text-muted-foreground">{m.membership_id}</span></span>
                <span className="flex items-center gap-3 text-xs text-muted-foreground">{[m.tshirts ? "T-shirt" : "", m.cloth ? "Cloth" : ""].filter(Boolean).join(" + ")}<GiftBadge status={m.status} /></span>
              </li>
            ))}
            {!data.members.length && <li className="py-6 text-center text-sm text-muted-foreground">No souvenirs given to members yet. Use the Give to Members tab.</li>}
          </ul>
        </Card>
      )}
    </div>
  );
}
